/**
 * bulgeArc — one-truth polyline bulge → circular-arc geometry primitive.
 *
 * A LWPOLYLINE vertex carries a `bulge` per DXF: `bulge = tan(θ/4)` where θ is
 * the signed included angle of the arc replacing the straight segment to the
 * NEXT vertex. Per the Autodesk DXF reference (group 42) the bulge is "made
 * NEGATIVE if the arc goes CLOCKWISE from the start point to the endpoint", so
 * a POSITIVE bulge sweeps COUNTER-CLOCKWISE about the arc centre. Traversing
 * CCW puts the centre to the LEFT of the chord direction and therefore bows the
 * arc itself to the RIGHT of it (Y-up frame): `v1=(0,0) v2=(20,0) bulge=+1`
 * is the LOWER half-circle through (10,−10), NOT the upper one. A bulge of 0 is
 * a straight edge.
 *
 * This module is the SINGLE source of truth for converting a (v1, v2, bulge)
 * triple into the arc's centre / radius / start-angle / signed-sweep. It lives
 * in `drawing-model` (the lowest geometry package) precisely so BOTH:
 *   - `command-engine/pathSampling.ts` (ARRAYPATH sampler, GRIPS seg-midpoint),
 *   - `drawing-model/viewportMembership.ts` (conservative model-space AABB),
 * consume one implementation rather than each re-deriving the (subtle, sign-
 * sensitive) bulge trigonometry. `command-engine` already depends on
 * `drawing-model`, so this placement keeps the dependency graph acyclic
 * (CLAUDE.md ПРИОРИТЕТ #2) — the reverse (importing the existing copy out of
 * `command-engine` into `drawing-model`) would introduce a cycle.
 *
 * Reference: Autodesk DXF reference (LWPOLYLINE group 42 bulge) + ezdxf 1.4.4
 * `ezdxf.math.bulge_to_arc`, which for `v1=(0,0) v2=(20,0) bulge=+1` returns
 * `(centre=(10,0), start=180°, end=0°, r=10)` — a CCW sweep 180°→360° through
 * (10,−10) — and whose `Path` flattening of the same LWPOLYLINE spans
 * `y ∈ [−10, 0]`. The repository's own render decoder
 * (`apps/web/…/scene3d/adapters/arcEllipseSpline.ts::bulgeToArc`, `ccw =
 * sign(bulge) > 0`) agrees. The sign conventions are pinned against that
 * external oracle by `bulgeArc.orientation.property.test.ts` and by the
 * reference case in `pathSampling.test.ts`.
 *
 * HISTORY: until the r17 P1 fix this module was MIRRORED through the chord —
 * it placed the centre on the wrong side for every non-semicircular bulge and
 * reported a CW `signedSweep` for a positive bulge. Consumers that predate the
 * fix may still carry comments about "mirrored"/"chord-symmetric" workarounds;
 * those workarounds are obsolete, not load-bearing.
 */

export type BulgePoint = { x: number; y: number };

/**
 * Resolved circular-arc parameters for a single bulged polyline segment.
 *
 * Angles are RADIANS. `startAngle` is the angle (about the centre) of `v1`;
 * the arc is swept by `signedSweep`, which carries the SIGN OF THE BULGE:
 * positive bulge ⇒ positive sweep ⇒ counter-clockwise about the centre,
 * negative bulge ⇒ clockwise (see the module doc reference case).
 * A point at parameter u ∈ [0,1] along the arc is
 * `(cx + r·cos(startAngle + signedSweep·u), cy + r·sin(...))`.
 */
export type BulgeArc = {
  cx: number;
  cy: number;
  radius: number;
  /** Angle of v1 about the centre, radians. */
  startAngle: number;
  /** Signed sweep, radians. `= 4·atan(bulge)`; positive ⇒ CCW about the centre. */
  signedSweep: number;
  /** Arc length = radius · |signedSweep|. */
  length: number;
};

const EPS = 1e-12;

/**
 * Convert a (v1, v2, bulge) triple into its circular-arc parameters, or `null`
 * when the segment is effectively straight (|bulge| ~ 0) or degenerate (the two
 * vertices coincide). One-truth geometry: callers MUST NOT re-derive this math.
 */
export function bulgeArc(
  v1: BulgePoint,
  v2: BulgePoint,
  bulge: number,
): BulgeArc | null {
  if (Math.abs(bulge) < EPS) return null;

  const dx = v2.x - v1.x;
  const dy = v2.y - v1.y;
  const chord = Math.hypot(dx, dy);
  // EQUIVALENT MUTANT: EqualityOperator, `<` -> `<=`. The two differ only for a
  // chord of EXACTLY 1e-12 — a single representable double. Every generator in
  // `tests/support/cadArbitraries` quantises coordinates to `CAD_COORD_FLOOR`
  // (2^ceil(log2(1e3 * COINCIDENT_EPS)), many orders above 1e-12), and a real
  // DXF never authors one either; a chord AT the epsilon is degenerate under
  // both readings (the arc it would imply has radius ~1e12 * chord).
  // Stryker disable next-line EqualityOperator: differs only at chord === 1e-12 exactly (see above)
  if (chord < EPS) return null;

  // bulge = tan(θ/4) ⇒ θ = 4·atan(bulge); θ is the signed included angle.
  const theta = 4 * Math.atan(bulge);
  // |R| = chord / (2·|sin(θ/2)|).
  const absRadius = Math.abs(chord / (2 * Math.sin(theta / 2)));

  const mx = (v1.x + v2.x) / 2;
  const my = (v1.y + v2.y) / 2;
  // `perp` = unit vector +90° CCW from the chord direction.
  const perpX = -dy / chord;
  const perpY = dx / chord;

  // Centre offset along ±perp from the chord midpoint, = radius·cos(θ/2) taken
  // in the sign of the bulge — identical to ezdxf's
  // `polar(start, angle(start,end) + (π/2 − 2·atan(bulge)), signed_radius)` and
  // to the repo's own render decoder. A POSITIVE bulge (CCW sweep) puts the
  // centre to the LEFT of the chord direction, so the arc bows to the RIGHT.
  // A major arc (|θ| > π) flips cos(θ/2) negative on its own, moving the centre
  // across the chord to the arc's own side — no separate branch needed.
  // EQUIVALENT MUTANT: EqualityOperator, `>=` -> `>`. The two differ only at
  // `bulge === 0` (and `-0`), and the `Math.abs(bulge) < EPS` guard at the top
  // of this function has already returned `null` for both. Unreachable.
  // Stryker disable next-line EqualityOperator: bulge === 0 already returned null above
  const bulgeSign = bulge >= 0 ? 1 : -1;
  const centerOffset = bulgeSign * absRadius * Math.cos(theta / 2);
  const cx = mx + perpX * centerOffset;
  const cy = my + perpY * centerOffset;

  const startAngle = Math.atan2(v1.y - cy, v1.x - cx);
  // θ = 4·atan(bulge) is ALREADY signed with the bulge, and per the DXF group-42
  // contract a positive bulge runs CCW. The signed sweep is therefore θ itself;
  // writing it as `bulgeSign · |θ|` states the same value more explicitly.
  const signedSweep = bulgeSign * Math.abs(theta);

  return {
    cx,
    cy,
    radius: absRadius,
    startAngle,
    signedSweep,
    length: absRadius * Math.abs(theta),
  };
}

/**
 * The CCW-ASCENDING angle pair a DXF `ARC` (group 50 / 51) needs to draw the
 * very point set {@link bulgeArc} / {@link bulgeArcPointAt} describe.
 *
 * A DXF ARC is ALWAYS traversed counter-clockwise from `startAngle` to
 * `endAngle` about its centre, while a {@link BulgeArc} carries a SIGNED sweep
 * and may run clockwise. The conversion is therefore not "copy the two angles":
 * when `signedSweep` is negative the point set ascends CCW from
 * `startAngle + signedSweep` back up to `startAngle`, so the pair must be
 * SWAPPED — exactly the rule ezdxf 1.4.4 `ezdxf.math.bulge_to_arc` states in
 * its own docstring ("the return values always describe a counter-clockwise
 * oriented arc, so for clockwise arcs the start and end angles are swapped").
 *
 * `startRad` is normalised into `[0, 2π)`; `endRad` is `startRad + |sweep|` and
 * is deliberately NOT re-normalised, so a sweep that crosses +X keeps
 * `endRad > startRad` and the pair states the sweep rather than leaving a
 * consumer to re-infer it modulo 2π.
 *
 * ONE TRUTH, two consumers: the block-child render explode
 * (`apps/web/…/scene3d/adapters/typed/blockBulgeExplode.ts`, which emits the
 * Arc / Ellipse pieces a non-uniform INSERT needs) and the top-level heavy
 * POLYLINE explode (`command-engine/explodeHeavyPolyline.ts`, which emits the
 * persisted `Arc` members). A second copy of this swap is how a bulged span
 * ends up drawn on one side of its chord and exploded to the other.
 */
export function bulgeArcCcwAngles(arc: BulgeArc): {
  startRad: number;
  endRad: number;
} {
  const sweep = Math.abs(arc.signedSweep);
  // EQUIVALENT MUTANT: EqualityOperator, `>=` -> `>`. `signedSweep` is
  // `±|4·atan(bulge)|` for a `bulge` that passed the `|bulge| >= EPS` guard, so
  // it is NEVER 0 — the only value at which the two operators disagree. (The
  // same suppression `command-engine/polylineOffsetRing.ts` carries for the
  // same expression, for the same reason.)
  // `disable`/`restore` rather than `disable next-line`: prettier wraps this
  // statement, so the operator sits on the line AFTER the declaration and a
  // next-line directive would guard the wrong one.
  // Stryker disable EqualityOperator: signedSweep is never 0 for a resolved arc (see above)
  const raw =
    arc.signedSweep >= 0 ? arc.startAngle : arc.startAngle + arc.signedSweep;
  // Stryker restore EqualityOperator
  // Normalise into [0, 2π). Written inline rather than imported: `bulgeArc.ts`
  // is the LOWEST geometry leaf (its module doc explains why it may not reach
  // up into `command-engine` or `apps/web`), and the only other copy of this
  // two-line modulo — `scene3d/adapters/typed/blockInsertTransform.normalizeRad`
  // — lives above it in the dependency graph.
  const TWO_PI_LOCAL = Math.PI * 2;
  const mod = raw % TWO_PI_LOCAL;
  const startRad = mod < 0 ? mod + TWO_PI_LOCAL : mod;
  return { startRad, endRad: startRad + sweep };
}

/** A point on the arc at parameter u ∈ [0, 1] (u=0 ⇒ v1, u=1 ⇒ v2). */
export function bulgeArcPointAt(arc: BulgeArc, u: number): BulgePoint {
  const angle = arc.startAngle + arc.signedSweep * u;
  return {
    x: arc.cx + arc.radius * Math.cos(angle),
    y: arc.cy + arc.radius * Math.sin(angle),
  };
}

/**
 * Axis-aligned bounding box of ONE polyline segment from `v1` to `v2`.
 *
 * For a straight segment (|bulge| ~ 0) this is the endpoint box. For a bulged
 * segment it is the box of the swept ARC — the two endpoints PLUS every
 * cardinal extremum (the arc's eastmost / northmost / westmost / southmost
 * points, at centre-relative angles 0 / 90 / 180 / 270°) that the sweep
 * actually crosses. A bulged arc bows OUTSIDE the chord/vertex hull, so the
 * cardinal extrema are required for a CONSERVATIVE box that never under-reports
 * the segment's extent (the membership window test must never drop a visible
 * entity — CLAUDE.md ПРИОРИТЕТ #1).
 */
export function bulgeSegmentBounds(
  v1: BulgePoint,
  v2: BulgePoint,
  bulge: number,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const arc = bulgeArc(v1, v2, bulge);
  if (!arc) {
    return {
      minX: Math.min(v1.x, v2.x),
      minY: Math.min(v1.y, v2.y),
      maxX: Math.max(v1.x, v2.x),
      maxY: Math.max(v1.y, v2.y),
    };
  }

  let minX = Math.min(v1.x, v2.x);
  let minY = Math.min(v1.y, v2.y);
  let maxX = Math.max(v1.x, v2.x);
  let maxY = Math.max(v1.y, v2.y);

  const start = arc.startAngle;
  const sweep = arc.signedSweep;
  // For each cardinal direction, find whether its angle lies within the swept
  // interval [start, start+sweep] (which may run CW, i.e. sweep < 0). We test
  // the offset of the cardinal angle from `start` measured in the sweep's
  // direction: it is on the arc iff that offset ∈ [0, |sweep|].
  const TWO_PI = Math.PI * 2;
  // EQUIVALENT MUTANT: EqualityOperator, `>=` -> `>`. Same invariant as
  // `bulgeArcCcwAngles` above — `arc.signedSweep` is never 0 here, because this
  // branch only runs when `bulgeArc` returned a non-null arc.
  // Stryker disable next-line EqualityOperator: signedSweep is never 0 for a resolved arc
  const dir = sweep >= 0 ? 1 : -1;
  const mag = Math.abs(sweep);
  // EQUIVALENT MUTANT: EqualityOperator, `<` -> `<=`. A fifth iteration would
  // test `cardinal = 4·(π/2) = 2π`, whose cos/sin are the `k = 0` cardinal's
  // (angle 0) to within a double — the SAME point, already merged into the box
  // by the first iteration. A repeated min/max merge is idempotent.
  // Stryker disable next-line EqualityOperator: k === 4 is the k === 0 cardinal again (2π ≡ 0); the merge is idempotent
  for (let k = 0; k < 4; k++) {
    const cardinal = (k * Math.PI) / 2; // 0, 90, 180, 270 degrees
    // Offset from start in the direction of travel, normalised to [0, 2π).
    let offset = dir * (cardinal - start);
    offset = ((offset % TWO_PI) + TWO_PI) % TWO_PI;
    // EQUIVALENT MUTANTS on this line: EqualityOperator `<=` -> `<`, and
    // ArithmeticOperator `+` -> `-`. Both only move the acceptance window by
    // ±1e-12 RADIANS at the sweep's own far end. A cardinal sitting exactly at
    // `offset === mag` IS the segment's end vertex, which the box was already
    // seeded with from `min/max(v1, v2)` above, so admitting or rejecting it
    // cannot change the answer; a cardinal within 1e-12 rad of it differs from
    // that vertex by `radius · 1e-12`, below every tolerance this box feeds.
    // Stryker disable next-line EqualityOperator: offset === mag is the end vertex, already in the seeded box
    // Stryker disable next-line ArithmeticOperator: a ±1e-12 rad window shift at the sweep end; see above
    if (offset <= mag + EPS) {
      const px = arc.cx + arc.radius * Math.cos(cardinal);
      const py = arc.cy + arc.radius * Math.sin(cardinal);
      // EQUIVALENT MUTANTS (four lines): EqualityOperator `<` -> `<=` and
      // `>` -> `>=`. On equality the assignment writes the value the variable
      // already holds — a no-op. No observable difference, ever.
      // Stryker disable next-line EqualityOperator: assignment on equality is a no-op
      if (px < minX) minX = px;
      // Stryker disable next-line EqualityOperator: assignment on equality is a no-op
      if (py < minY) minY = py;
      // Stryker disable next-line EqualityOperator: assignment on equality is a no-op
      if (px > maxX) maxX = px;
      // Stryker disable next-line EqualityOperator: assignment on equality is a no-op
      if (py > maxY) maxY = py;
    }
  }

  return { minX, minY, maxX, maxY };
}
